import React, { useEffect } from 'lib/react';
import { useTranslation } from 'lib/translation';
import { useDispatch, useSelector } from 'lib/redux';
import { Switch, Route } from 'lib/router';
import {
  EVGrid,
  EVTab,
  EVTabs,
  EVCheckCircleOutlineIcon,
} from 'lib/components';
import ValidationMessageDialog from 'commonComponents/validationMessageDialog';
import { COUNTRY, INGREDIENTS } from 'constants/AppConstants';
import useMediaQuery from 'hooks/useMediaQuery';
import history from 'store/CreateHistory';
import {
  FINALIZE_ORDER_ROUTE, ORDER_BLUEPRINT_ROUTE, ORDER_ROUTE, PLACE_ORDER_ROUTE,
} from 'constants/RouteConstants';
import Banner from 'commonComponents/banner';
import { isOrderingDisabledSelector } from 'utils/appSelectors.utils';
import { isAuthorized, isEntitledForEvOneIngredient } from 'utils/auth.utils';
import OrderStyles from './OrderStyles';
import AddressSearch from './components/AddressSearch.component';
import DuplicateReportsDialog from './components/DuplicateReports.component';
import AddressSearchOrder from './AddressSearchOrder.component';
import {
  closeValidationMessageDialog, loadOrderPage, setCountryAndClearAddress,
  cancelOrderSelection, setShowMap,
  validateSRSBillingAffiliatedCustomer, setPanelType,
} from './Order.actions';
import BlueprintOrder from './components/blueprintOrder/BlueprintOrder.component';
import { PANEL_TYPES } from './Order.constants';

function Order() {
  const isMobile = useMediaQuery(640);
  const { t } = useTranslation();
  const {
    showMap, validationMessageType, isOrderFlowStarted, orderPageAlert,
    isSRSBillingAffiliate, srsValidation,
    myevRefreshSrsBilling, mapPoints, bluePrintFileId, entitlements,
  } = useSelector(({
    order, userData, featureFlag, srs, orderAddresses, placeOrder, userEntitlements,
  }) => ({
    mapPoints: orderAddresses.mapPoints,
    showMap: order.showMap,
    validationMessageType: order.validationMessageType,
    isOrderFlowStarted: order.isOrderFlowStarted,
    orderPageAlert: order.orderPageAlert,
    isSRSBillingAffiliate: userData.initParameters?.isSRSBillingAffiliate,
    srsValidation: srs.srsValidation,
    myevRefreshSrsBilling: featureFlag.myevRefreshSrsBilling,
    bluePrintFileId: placeOrder.bluePrintFileId,
    entitlements: userEntitlements,
  }));

  const isOrderingDisabled = useSelector(isOrderingDisabledSelector);
  const classes = OrderStyles();
  const dispatch = useDispatch();

  // Check if user is entitled for PROPERTY_DATA_PRE_INSPECTION ingredient
  const hasPropertyDataPreInspectionEntitlement = isEntitledForEvOneIngredient(entitlements, INGREDIENTS.PROPERTY_DATA_PRE_INSPECTION);

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const countryFromSearchParams = urlSearchParams.get(COUNTRY);
    if (countryFromSearchParams) {
      dispatch(setCountryAndClearAddress(countryFromSearchParams, false));
    }
    dispatch(loadOrderPage());

    // Block transitions from order page to clear the selections for current order
    history.unblock = history.block((tx) => {
      dispatch(cancelOrderSelection());
      history.unblock();
      history.push(tx.pathname);
    });

    return () => {
      history.unblock();
      delete history.unblock;
    };
  }, []);

  useEffect(() => {
    // Block transitions from order page to clear the selections for current order
    history.unblock = history.block((tx) => {
      dispatch(cancelOrderSelection());
      history.unblock();
      history.push(tx.pathname);
    });

    return () => {
      if (history.unblock) {
        history.unblock();
        delete history.unblock;
      }
    };
  }, [isOrderFlowStarted]);

  useEffect(() => {
    if (myevRefreshSrsBilling // SRS feature flag check
        && (mapPoints.length > 0 || bluePrintFileId) // SRS check to trigger after address search
        && isSRSBillingAffiliate // SRS check to trigger for SRS billing affiliate
        && srsValidation && !srsValidation.isLoading && !srsValidation.checked) {
      dispatch(validateSRSBillingAffiliatedCustomer());
    }
  }, [mapPoints, bluePrintFileId, isSRSBillingAffiliate]);

  // Reset panel type when order flow starts
  useEffect(() => {
    if (isOrderFlowStarted) {
      // Only set PRE_PANEL if user has PROPERTY_DATA_PRE_INSPECTION entitlement, otherwise set STRUCTURE_MEASUREMENTS
      const panelType = hasPropertyDataPreInspectionEntitlement ? PANEL_TYPES.PRE_PANEL : PANEL_TYPES.STRUCTURE_MEASUREMENTS;
      dispatch(setPanelType(panelType));
    }
  }, [isOrderFlowStarted, dispatch, hasPropertyDataPreInspectionEntitlement]);

  const handleValidationDialogNext = () => {
    history.unblock();
    history.push(FINALIZE_ORDER_ROUTE);
  };

  const handleValidationDialogClose = () => dispatch(closeValidationMessageDialog());
  const validationDialogProps = {
    CLOSED_VALIDATION: {
      open: false,
      title: '',
      message: '',
    },
    NO_SELECTEDPRODUCTS_VALIDATION: {
      open: true,
      title: t('order_noselectedproductsvalidation_title'),
      message: t('order_noselectedproductsvalidation_content'),
      okayButtonText: t('common_okay'),
    },
    COMPLEX_MARKED_VALIDATION: {
      open: true,
      title: t('order_complexmarkedvalidation_title'),
      message: (
        <>
          {t('order_complexmarkedvalidation_content_1')}
          <b>{t('order_complexmarkedvalidation_content_2')}</b>
          {t('order_complexmarkedvalidation_content_3')}
          <b>{t('order_complexmarkedvalidation_content_4')}</b>
          .
        </>
      ),
      cancelButtonText: t('order_complexmarkedvalidation_content_4'),
      okayButtonText: t('order_complexmarkedvalidation_content_2'),
      isNext: true,
    },
    NO_SELECTEDPIN_VALIDATION: {
      open: true,
      title: t('order_noselectedpinvalidation_title'),
      message: t('order_noselectedpinvalidation_content'),
      okayButtonText: t('common_okay'),
      isNext: false,
    },
    NO_BLUEPRINTID_VALIDATION: {
      open: true,
      title: t('order__noblueprintid_validation_title'),
      message: t('order__noblueprintid_validation_content'),
      okayButtonText: t('common_okay'),
      isNext: false,
    },
  };

  const handleMapDisplayChange = () => {
    dispatch(setShowMap(!showMap));
  };

  const isBluePrintOrder = history.location.pathname === ORDER_BLUEPRINT_ROUTE;
  const showAddressSearchRow = !isBluePrintOrder || !isMobile;

  return (
    <>
      {!isOrderingDisabled && <DuplicateReportsDialog />}
      <ValidationMessageDialog
        {...validationDialogProps[validationMessageType]}
        handleClose={handleValidationDialogClose}
        handleNext={handleValidationDialogNext}
      />
      {orderPageAlert.open && (
        <Banner
          severity={orderPageAlert.severity}
          iconMapping={{ success: <EVCheckCircleOutlineIcon /> }}
          message={orderPageAlert.message}
        />
      )}
      <EVGrid className={classes.mainContainer}>
        {isMobile && isAuthorized() && (
        <EVTabs
          value={history.location.pathname}
          textColor="inherit"
          indicatorColor="primary"
          className={classes.searchTabs}
          onChange={(_, path) => history.push(path)}
        >
          <EVTab className={classes.tabHeading} label={t('SearchByLocation')} value={ORDER_ROUTE} />
          {!isOrderingDisabled && <EVTab className={classes.tabHeading} label={t('common__upload_blueprint')} value={ORDER_BLUEPRINT_ROUTE} />}
        </EVTabs>
        )}

        {showAddressSearchRow && <AddressSearch />}
        <Switch>
          <Route exact path={ORDER_BLUEPRINT_ROUTE} component={BlueprintOrder} />
          <Route exact path={ORDER_ROUTE} render={() => <AddressSearchOrder showMap={showMap} setShowMap={handleMapDisplayChange} />} />
          <Route exact path={PLACE_ORDER_ROUTE} render={() => <AddressSearchOrder showMap={showMap} setShowMap={handleMapDisplayChange} />} />
        </Switch>
      </EVGrid>
    </>
  );
}

export default Order;
